import { LoadingButton } from "@mui/lab";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CloseIconCircle, CrossIcon } from "../../assets/icons";
import useAllergens from "../../hooks/useAllergens";
import {
  AddressResponse,
  DiscountRequest,
  DiscountResponse,
  PartnerUserDetailResponse,
  PartnerUserDetailResponseGenderEnum,
  RegisterUserByAdminRequest,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { setNotification } from "../../store/notificationSlice";
import { RadioBoxCustom } from "../RadioBox";
import Tagging from "../Tagging";
import Transition from "../Transition";
import useValidation from "../Validation";
import useAddressValidation from "../Validation/adress";
import Api from "../../openapi";
import moment, { Moment } from "moment";
import { checkEmpty, checkIf, defaultFormat, disableDayCheck, displayFormat } from "../../utils";
import { Toggle } from "../Switch";

interface IDiscountCrud extends IDialogProps {
  discount?: DiscountResponse;
}
interface UpdatePlanRequest extends DiscountRequest {
  id?: string;
}

export default function DiscountCrud(props: IDiscountCrud) {
  const dummyDiscount = {
    active: true,
    code: "",
    percentage: "",
    amount: "",
    minPurchaseAmount: undefined,
    maxDiscountAmount: undefined,
    startDate: "",
    endDate: "",
    appliesToMealPrice: true,
    appliesToDeliveryFee: true,
    availableForAppSale: true,
  };
  const dispatch = useDispatch();
  const [discount, setDiscountEdit] = useState<any>(dummyDiscount);
  const { validation, setValidation } = useValidation(discount);
  const [loading, setLoading] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  const [startDate, setStartDate] = useState<Moment | null>(discount.startDate ? moment(discount.startDate) : null);
  const [endDate, setEndDate] = useState<Moment | null>(discount.endDate ? moment(discount.endDate) : null);
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);
  const calendarRef = useRef<any>(null);
  const [dateSelectionError, setDateSelectionError] = useState<DateValidationError | null>(null);
  const errorMessage = useMemo(() => {
    switch (dateSelectionError) {
      case "shouldDisableDate": {
        return "Invalid date";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }
      default: {
        return "";
      }
    }
  }, [dateSelectionError]);

  const handleFieldChange = (field: string, value: any) => {
    setFormDirty(true);
    setDiscountEdit((state: any) => ({ ...state, [field]: value }));
  };

  const handleChangeStartDate = (newValue: Moment | null) => {
    setStartDate(newValue as Moment);
    if (newValue === null) {
      handleFieldChange("startDate", "");
    } else {
      handleFieldChange("startDate", moment(newValue).format(defaultFormat));
    }
  };
  const handleChangeEndDate = (newValue: Moment | null) => {
    setEndDate(newValue as Moment);
    if (newValue === null) {
      handleFieldChange("endDate", "");
    } else {
      handleFieldChange("endDate", moment(newValue).format(defaultFormat));
    }
  };

  const resetForm = () => {
    setStartDate(null);
    setEndDate(null);
    setDiscountEdit(dummyDiscount);
    setValidation((state) => ({ ...state, show: false }));
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    const createDiscountDetail: any = {
      active: discount.active,
      code: discount.code,
      percentage: checkEmpty(discount.percentage) ? undefined : Math.round(discount.percentage * 10e4) / 10e6,
      amount: checkEmpty(discount.amount) ? undefined : Number(discount.amount),
      minPurchaseAmount: checkEmpty(discount.minPurchaseAmount) ? undefined : Number(discount.minPurchaseAmount),
      maxDiscountAmount: checkEmpty(discount.maxDiscountAmount) ? undefined : Number(discount.maxDiscountAmount),
      startDate: !checkEmpty(discount.startDate) ? moment(discount.startDate).format(defaultFormat) : undefined,
      endDate: !checkEmpty(discount.endDate) ? moment(discount.endDate).format(defaultFormat) : undefined,
      appliesToMealPrice: discount.appliesToMealPrice,
      appliesToDeliveryFee: discount.appliesToDeliveryFee,
      availableForAppSale: discount.availableForAppSale,
    };

    try {
      setLoading(true);

      if (props.create) {
        const res = await Api.Discount.createDiscount(createDiscountDetail);
        dispatch(
          setNotification({
            show: true,
            severity: "success",
            message: "Discount has been created successfully",
          })
        );
        resetForm();
        props.onConfirm(true);
        props.onClose({});
      } else if (props.edit) {
        const res = await Api.Discount.updateDiscount(discount.id, createDiscountDetail);
        props.onConfirm(true);
        dispatch(
          setNotification({
            show: true,
            severity: "success",
            message: "Discount has been updated successfully",
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
    setFormDirty(false);
  };

  //   console.log("validation", validation);
  //   console.log("discount", discount);

  useEffect(() => {
    if (props.discount) {
      const withUpdatedPercentage = {
        ...props.discount,
        percentage: checkIf(props.discount.percentage)
          ? Math.round(Number(props.discount.percentage) * 10e6) / 10e4
          : null,
      };
      setDiscountEdit(withUpdatedPercentage);
      const { startDate, endDate } = props.discount;
      checkIf(startDate) && setStartDate(moment(startDate));
      checkIf(endDate) && setEndDate(moment(endDate));
    }
  }, [props.discount]);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "820px",
            maxWidth: "820px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Code
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={discount.code}
                  error={validation.show && validation.code?.error}
                  helperText={validation.show && validation.code?.error ? validation.code.helperText : undefined}
                  onChange={(e) => handleFieldChange("code", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Amount
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={discount.amount}
                  error={validation.show && validation.amount?.error}
                  helperText={validation.show && validation.amount?.error ? validation.amount.helperText : undefined}
                  onChange={(e) => handleFieldChange("amount", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Percentage
                </Typography>
                <TextField
                  className="form-field"
                  value={discount.percentage}
                  error={validation.show && validation.percentage?.error}
                  helperText={
                    validation.show && validation.percentage?.error ? validation.percentage.helperText : undefined
                  }
                  onChange={(e) => handleFieldChange("percentage", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Minimum Purchase Amount
                </Typography>
                <TextField
                  className="form-field"
                  value={discount.minPurchaseAmount}
                  error={validation.show && validation.minPurchaseAmount?.error}
                  helperText={
                    validation.show && validation.minPurchaseAmount?.error
                      ? validation.minPurchaseAmount.helperText
                      : undefined
                  }
                  onChange={(e) => handleFieldChange("minPurchaseAmount", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Maximum Discount Amount
                </Typography>
                <TextField
                  className="form-field"
                  value={discount.maxDiscountAmount}
                  error={validation.show && validation.maxDiscountAmount?.error}
                  helperText={
                    validation.show && validation.maxDiscountAmount?.error
                      ? validation.maxDiscountAmount.helperText
                      : undefined
                  }
                  onChange={(e) => handleFieldChange("maxDiscountAmount", e.target.value)}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Start Date
                </Typography>
                <DatePicker
                  sx={{
                    // "& > .MuiFormHelperText-root": { marginTop: "2px" },
                    "& .MuiFormHelperText-root": {
                      bottom: "-20px",
                      // position: "absolute",
                      left: 0,
                      right: 0,
                      // textAlign: "center",
                      color: "text.error",
                    },
                  }}
                  value={startDate}
                  open={open1}
                  format={displayFormat}
                  onChange={(newValue: Moment | null) => handleChangeStartDate(newValue as Moment)}
                  onAccept={() => setOpen1(false)}
                  onClose={() => setOpen1(false)}
                  onError={(newError: DateValidationError) => setDateSelectionError(newError)}
                  //   shouldDisableDate={(day: Moment) => moment(day).startOf("day").isBefore(moment().startOf("day"))}
                  ref={calendarRef}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      onClick: () => setOpen1(true),
                      helperText:
                        validation.show && validation.startDate?.error
                          ? validation.startDate.helperText
                          : errorMessage || null,
                    },
                  }}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  End Date
                </Typography>
                <DatePicker
                  sx={{
                    // "& > .MuiFormHelperText-root": { marginTop: "2px" },
                    "& .MuiFormHelperText-root": {
                      bottom: "-20px",
                      // position: "absolute",
                      left: 0,
                      right: 0,
                      // textAlign: "center",
                      color: "text.error",
                    },
                  }}
                  value={endDate}
                  open={open2}
                  format={displayFormat}
                  onChange={(newValue: Moment | null) => handleChangeEndDate(newValue as Moment)}
                  onAccept={() => setOpen2(false)}
                  onClose={() => setOpen2(false)}
                  onError={(newError: DateValidationError) => setDateSelectionError(newError)}
                  //   shouldDisableDate={(day: Moment) => moment(day).startOf("day").isBefore(moment().startOf("day"))}
                  ref={calendarRef}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      onClick: () => setOpen2(true),
                      helperText:
                        validation.show && validation.endDate?.error
                          ? validation.endDate.helperText
                          : errorMessage || null,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", justifyContent: "space-between" }}>
              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={
                  <Toggle
                    checked={Boolean(discount?.active)}
                    onChange={(e) => handleFieldChange("active", Boolean(e.target.checked))}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={
                  <Toggle
                    checked={Boolean(discount?.appliesToMealPrice)}
                    onChange={(e) => handleFieldChange("appliesToMealPrice", Boolean(e.target.checked))}
                  />
                }
                label="Apply to meal price"
              />

              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={
                  <Toggle
                    checked={Boolean(discount?.appliesToDeliveryFee)}
                    onChange={(e) => handleFieldChange("appliesToDeliveryFee", Boolean(e.target.checked))}
                  />
                }
                label="Apply to delivery fee"
              />
              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={
                  <Toggle
                    checked={Boolean(discount?.availableForAppSale)}
                    onChange={(e) => handleFieldChange("availableForAppSale", Boolean(e.target.checked))}
                  />
                }
                label="Available for app sale"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
